/**
 * Pagination bar for {@link DataTable} (page input + sibling window around the current page).
 */

import * as React from 'react';
import { ArrowRight, AlertCircle } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { Input } from '@/components/forms/Input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { getErrorMessage } from '@/utils/errors-utils';

/** Props for {@link DataTablePagination}. */
interface DataTablePaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  startIndex: number;
  endIndex: number;
  itemLabel: string;
  onPageChange: (page: number) => void;
}

/** Page numbers around the current page (first/last are reachable via the page input). */
function getPageItems(currentPage: number, totalPages: number, siblingCount = 1): number[] {
  if (totalPages <= 0) return [];
  if (totalPages <= siblingCount * 2 + 1) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const range = (start: number, end: number) =>
    Array.from({ length: Math.max(0, end - start + 1) }, (_, i) => start + i);

  let start = currentPage - siblingCount;
  let end = currentPage + siblingCount;

  if (start < 1) {
    end += 1 - start;
    start = 1;
  }
  if (end > totalPages) {
    start -= end - totalPages;
    end = totalPages;
  }
  start = Math.max(1, start);

  return range(start, end);
}

/** Pagination controls for a data table. */
export const DataTablePagination: React.FC<DataTablePaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  startIndex,
  endIndex,
  itemLabel,
  onPageChange,
}) => {
  const gotoInputId = React.useId();
  const isFirstPage = currentPage <= 1;
  const isLastPage = currentPage >= totalPages || totalPages === 0;
  const safeTotalPages = Math.max(totalPages, 1);
  const pageItems = getPageItems(currentPage, totalPages);

  const [goToValue, setGoToValue] = React.useState(String(currentPage));
  const [inputError, setInputError] = React.useState<string | undefined>();

  React.useEffect(() => {
    setGoToValue(String(currentPage));
    setInputError(undefined);
  }, [currentPage]);

  const goToPage = (page: number) => {
    if (!Number.isFinite(page) || totalPages <= 0) return;
    const clamped = Math.min(Math.max(Math.trunc(page), 1), totalPages);
    if (clamped !== currentPage) {
      onPageChange(clamped);
    }
    setGoToValue(String(clamped));
  };

  /** Jump to a typed page; restore the current page if the input is empty/NaN. */
  const handleGoSubmit = (event: React.SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsed = Number.parseInt(goToValue, 10);
    if (Number.isNaN(parsed) || goToValue.trim() === '') {
      setGoToValue(String(currentPage));
      setInputError(undefined);
      return;
    }
    if (parsed < 1 || parsed > totalPages) {
      setInputError(getErrorMessage({ code: 'pageOutOfRange', min: 1, max: safeTotalPages }));
      return;
    }
    setInputError(undefined);
    goToPage(parsed);
  };

  return (
    <div className="table-pagination">
      <p className="table-pagination-info">
        Affichage{' '}
        <strong>
          {startIndex + 1}-{Math.min(endIndex, totalItems)}
        </strong>{' '}
        sur <strong>{totalItems}</strong> {itemLabel}
      </p>

      <nav className="table-pagination-controls" aria-label="Pagination">
        <div className="table-pagination-buttons">
          <Button preset="pagination-first" onClick={() => goToPage(1)} disabled={isFirstPage} />
          <Button
            preset="pagination-prev"
            onClick={() => goToPage(currentPage - 1)}
            disabled={isFirstPage}
          />

          {pageItems.map((item) => (
            <Button
              key={item}
              preset="pagination-page"
              active={currentPage === item}
              onClick={() => goToPage(item)}
              aria-label={`Page ${item}`}
              aria-current={currentPage === item ? 'page' : undefined}
            >
              {item}
            </Button>
          ))}

          <Button
            preset="pagination-next"
            onClick={() => goToPage(currentPage + 1)}
            disabled={isLastPage}
          />
          <Button
            preset="pagination-last"
            onClick={() => goToPage(totalPages)}
            disabled={isLastPage}
          />
        </div>

        <p className="table-pagination-count" aria-live="polite">
          Page <strong>{Math.min(currentPage, safeTotalPages)}</strong> sur{' '}
          <strong>{safeTotalPages}</strong>
        </p>

        <form className="table-pagination-goto" onSubmit={handleGoSubmit}>
          <label htmlFor={gotoInputId} className="table-pagination-goto-label">
            Aller à
          </label>
          <TooltipProvider delayDuration={0}>
            <Tooltip open={!!inputError}>
              <TooltipTrigger asChild>
                <div className="w-16">
                  <Input
                    id={gotoInputId}
                    type="number"
                    inputMode="numeric"
                    inputSize="sm"
                    value={goToValue}
                    onChange={(event) => {
                      setGoToValue(event.target.value);
                      setInputError(undefined);
                    }}
                    onBlur={() => {
                      if (goToValue.trim() === '') {
                        setGoToValue(String(currentPage));
                        setInputError(undefined);
                      }
                    }}
                    disabled={totalPages <= 0}
                    aria-label={`Aller à la page (1-${safeTotalPages})`}
                  />
                </div>
              </TooltipTrigger>
              {inputError && (
                <TooltipContent
                  side="top"
                  className="text-destructive border-destructive flex items-center gap-1.5"
                >
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                  <p>{inputError}</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
          <Button
            type="submit"
            variant="ghost"
            size="icon"
            icon={ArrowRight}
            className="table-pagination-goto-button"
            disabled={totalPages <= 0}
            aria-label="Aller à la page"
            title="Aller à la page"
          />
        </form>
      </nav>
    </div>
  );
};
